import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { logger } from '../datadog/logger';
import { useVideoPlatform } from '../hooks/useVideoPlatform';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { roomAtom } from '../video-sdk/core/atoms';
import { admissionTrackingHistory } from './admission/analytics/AdmissionTrackingHistory';
import { inSessionPatientInformationAtom, providerShortIdAtom } from './state';

export function useProviderSessionViewedEvent(inSession: boolean) {
  const { userViewed } = useTrackEvent();
  const providerShortId = useAtomValue(providerShortIdAtom) ?? '';
  const inSessionPatientInformation = useAtomValue(inSessionPatientInformationAtom);
  const videoPlatform = useVideoPlatform();
  const hasLoggedSessionEnteredRef = useRef(false);
  const roomSid = useAtomValue(roomAtom)?.sid;

  useEffect(
    function resetSessionEnteredLogWhenLeavingSession() {
      if (!inSession) {
        hasLoggedSessionEnteredRef.current = false;
      }
    },
    [inSession],
  );

  useEffect(
    function emitProviderSessionEnteredSegmentEvent() {
      if (!inSession || hasLoggedSessionEnteredRef.current) return;

      const patientShortId = inSessionPatientInformation?.patientShortId ?? '';
      const appointmentShortId = inSessionPatientInformation?.inSessionAppointmentShortId ?? '';
      const admissionTransactionId =
        roomSid && admissionTrackingHistory.getByRoomSid(roomSid)?.admissionTransactionId;

      if (!admissionTransactionId) {
        logger.error(
          'Skipping tracking provider-joined-session-viewed event',
          { roomSid, patientShortId },
          new Error('Missing admission transaction ID'),
        );
        return;
      }

      userViewed(EventTypeEnum.JOINED_SESSION, {
        patientShortId,
        providerShortId,
        appointmentShortId,
        source: videoPlatform ?? inSessionPatientInformation?.videoPlatform ?? '',
        txId: admissionTransactionId,
      });
      hasLoggedSessionEnteredRef.current = true;
    },
    [inSession, inSessionPatientInformation, providerShortId, roomSid, userViewed, videoPlatform],
  );
}
